import { memo, useState } from 'react';
import { Avatar, Button, Card, Flex, Tag, theme, Tooltip } from 'antd';
import { FiEdit2, FiTrash2 } from 'react-icons/fi';
import { LuCircleHelp, LuLock } from 'react-icons/lu';
import styled from 'styled-components';

import fallbackImage from '@assets/fallbackImage.jpg';
import lightbulbTreeIcon from '@assets/lightbulb_tree.png';
import verifiedIcon from '@assets/biodrone_verified.svg';
import BaseText from '@components/typography/BaseText';
import { useThumbnailUrl } from '@hooks/useThumbnailUrl';
import { PROJECT_TYPE_LABELS } from '@utils/constants';
import { displayedAliases, isSystemNode } from '@utils/taxonomyTree';

import { TRAINING_GATE } from '../constants';
import { describeMissing } from '../objectTree';
import { formatCount, getTrainingGate } from './objectDetails/objectsUtils';

// System objects are Biodrone-maintained and read-only, so they sit back visually.
const SYSTEM_OPACITY = 0.72;

// TODO: turn on once the backend can edit (PATCH /taxonomy/nodes/{uuid}) and delete
// objects; only GET and POST exist so far. The edit form and its PATCH call are
// already built (ObjectDetails, updateTaxonomyNode); delete still needs both.
const OBJECT_ACTIONS_ENABLED = false;
const ALIAS_OPACITY = 0.7;

// The PNG is used as a mask so the icon takes the accent colour.
const TrainReadyIcon = styled.span`
	width: ${({ $size }) => $size}px;
	height: ${({ $size }) => $size}px;
	flex: none;
	background: ${({ $color }) => $color};
	mask: url(${lightbulbTreeIcon}) center / contain no-repeat;
`;

// What the card announces when not ready: without counts that is unknown, not "not ready".
const NOT_READY_DISPLAY = {
	[TRAINING_GATE.LOCKED]: { TrailingIcon: LuLock, prefix: 'Not ready to train' },
	[TRAINING_GATE.UNKNOWN]: { TrailingIcon: LuCircleHelp, prefix: 'Training readiness unknown' },
};

// The lightbulb and annotation total, green when the summary says a child can be
// trained; otherwise muted with a trailing lock, or a question mark while there are no
// counts (as in the details panel). The tooltip says what is missing.
const TrainingStatus = ({ object, mode }) => {
	const { token } = theme.useToken();

	const { status } = getTrainingGate(object);
	const isReady = status === TRAINING_GATE.READY;
	const { TrailingIcon, prefix } = NOT_READY_DISPLAY[status] ?? {};
	const missing = isReady ? null : describeMissing(object, mode);
	// The node's total from the summary; nothing until the summary lists it.
	const count = typeof object?.annotations === 'number' ? formatCount(object.annotations) : null;
	const statusText = isReady ? 'Ready to train a child object' : `${prefix}: ${missing}`;

	return (
		<Tooltip title={isReady ? 'Ready to train — you can add a child object here' : missing}>
			<Flex
				role="img"
				aria-label={count === null ? statusText : `${statusText}. ${count} annotations`}
				align="center"
				gap={token.sizeXS}
				style={{ flex: 'none' }}>
				{/* The lightbulb and its count read as one figure, so they sit closer. */}
				<Flex align="center" gap={token.sizeXXS}>
					<TrainReadyIcon
						aria-hidden
						$size={token.sizeMD}
						$color={isReady ? token.colorSuccess : token.colorTextTertiary}
					/>
					{count !== null && (
						<BaseText size="sm" strong type={isReady ? 'success' : 'secondary'}>
							{count}
						</BaseText>
					)}
				</Flex>
				{TrailingIcon && (
					<TrailingIcon
						size={token.sizeMD}
						color={token.colorTextTertiary}
						aria-hidden="true"
					/>
				)}
			</Flex>
		</Tooltip>
	);
};

const ObjectTreeCard = ({ object, mode, selected = false, onEdit, onDelete }) => {
	const { token } = theme.useToken();

	const thumbnailUrl = useThumbnailUrl(object.thumbnail);
	// Keyed to the URL, so a refreshed or changed thumbnail gets another chance to load.
	const [failedUrl, setFailedUrl] = useState(null);
	const thumbnailFailed = failedUrl !== null && failedUrl === thumbnailUrl;

	const isSystem = isSystemNode(object);
	const aliases = displayedAliases(object).join(' · ') || null;
	const projectType = PROJECT_TYPE_LABELS[mode];

	// The row itself selects the object, so the action buttons must not bubble.
	const handleAction = handler => event => {
		event.stopPropagation();
		handler?.(object);
	};

	return (
		<Card
			size="small"
			style={{
				borderColor: selected ? token.colorPrimary : undefined,
				opacity: isSystem ? SYSTEM_OPACITY : 1,
			}}>
			<Flex align="center" gap="small">
				<Avatar
					shape="square"
					alt=""
					size={token.sizeXXL}
					src={thumbnailFailed ? fallbackImage : (thumbnailUrl ?? fallbackImage)}
					onError={() => {
						setFailedUrl(thumbnailUrl);
						return false;
					}}
					style={{ flex: 'none' }}
				/>

				<Flex vertical flex={1} style={{ minWidth: 0 }}>
					<Flex align="center" gap="small" style={{ minWidth: 0 }}>
						<BaseText strong ellipsis type={isSystem ? 'secondary' : undefined}>
							{object.name}
						</BaseText>

						{isSystem ? (
							<Tooltip title="Biodrone Verified">
								<img
									src={verifiedIcon}
									alt="Biodrone Verified"
									width={token.sizeMS}
									height={token.sizeMS}
									style={{ flex: 'none' }}
								/>
							</Tooltip>
						) : (
							<Tag color="success" variant="filled">
								Custom
							</Tag>
						)}

						{/* Informational, not disabling: the object is still manageable, it just
						    has no model bound for the selected project type. Worded for
						    clients, who are not expected to know what a model is. */}
						{!object.is_available && (
							<Tooltip
								title={`“${object.name}” detection is not available in ${projectType ? `${projectType} projects` : 'this project type'} yet`}>
								<Tag variant="outlined">
									Not in {projectType ?? 'this project type'}
								</Tag>
							</Tooltip>
						)}
					</Flex>

					{object.description && (
						<BaseText size="sm" type="secondary" ellipsis>
							{object.description}
						</BaseText>
					)}

					{aliases && (
						<BaseText
							size="xs"
							type="success"
							ellipsis
							style={{ opacity: ALIAS_OPACITY }}>
							{aliases}
						</BaseText>
					)}
				</Flex>

				{/* Not detected here: the summary's counts span every project type, so the
				    tag is all the row can honestly say. */}
				{object.is_available && <TrainingStatus object={object} mode={mode} />}

				{/* Every non-system object, for members and admins alike. TODO: split them
				    once the app has an authorization layer — the backend may limit org
				    edits to admins. */}
				{OBJECT_ACTIONS_ENABLED && !isSystem && (
					// Set apart from the status: those are information, these are actions.
					<Flex
						gap={token.sizeXS}
						style={{ flex: 'none', marginInlineStart: token.marginXS }}>
						<Button
							size="small"
							color="default"
							variant="filled"
							icon={<FiEdit2 aria-hidden />}
							aria-label={`Edit ${object.name}`}
							onClick={handleAction(onEdit)}
						/>
						{/* Disabled until Objects wires a handler (there is no delete endpoint
						    yet). A disabled button ignores its own onClick, so the span keeps a
						    click on it from reaching the row and selecting it. */}
						<Tooltip
							title={onDelete ? undefined : 'Deleting objects is not available yet'}>
							<span onClick={event => event.stopPropagation()}>
								<Button
									size="small"
									color="danger"
									variant="filled"
									icon={<FiTrash2 aria-hidden />}
									aria-label={`Delete ${object.name}`}
									disabled={!onDelete}
									onClick={handleAction(onDelete)}
								/>
							</span>
						</Tooltip>
					</Flex>
				)}
			</Flex>
		</Card>
	);
};

// Memoised on a measured problem: rc-tree rebuilds every node's title on each of its
// render passes (expand state, motion start and end), so without this one expand or
// collapse re-rendered all 25 cards about eleven times — a second of main-thread time.
export default memo(ObjectTreeCard);
